/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const DirectionalLightCtor = (typeof globalThis !== "undefined" && globalThis.DirectionalLightNode) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const DIRECTIONAL_LIGHT_PROPS = [
  "color",
  "intensity",
  "castShadow",
  "shadowMapResolution",
  "shadowBias",
  "shadowRadius"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class DirectionalLightEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    DIRECTIONAL_LIGHT_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          
          // Special handling for color properties
          if (prop === "color" && this.sourceObject?.color?.set) {
            this.sourceObject.color.set(v);
          } else if (prop === "color" && this.sourceObject?.color?.isColor) {
            // Convert hex colors to THREE.Color for editor context
            if (typeof v === "string" || typeof v === "number") {
              this.sourceObject.color.set(v);
            } else if (v?.isColor) {
              this.sourceObject.color.copy(v);
            }
          } else if (prop === "shadowMapResolution" && Array.isArray(v) && v.length === 2) {
            // For shadowMapResolution, we need to directly set the shadow map size values
            this.sourceObject.shadow.mapSize.x = v[0];
            this.sourceObject.shadow.mapSize.y = v[1];
          } else if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
          
          // Call onChange if available
          if (typeof this.sourceObject.onChange === "function") {
            this.sourceObject.onChange();
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (prop === "shadowMapResolution" && sourceObject?.shadow?.mapSize) {
          initial = [sourceObject.shadow.mapSize.x, sourceObject.shadow.mapSize.y];
        } else if (prop === "color" && sourceObject?.color?.isColor) {
          // For color properties, ensure we store the actual THREE.Color object
          initial = sourceObject.color;
        } else if (d?.get) {
          initial = d.get.call(sourceObject);
        } else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) {
          initial = sourceObject[prop];
        }
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class DirectionalLightAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const DIRECTIONAL_LIGHT_COMPONENT = "directional-light";

    const setComponentProp = (component, key, val) => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(component) || {})
      };
      current[key] = val;
      this.sourceObject.el.setAttribute(component, current);
    };

    const getComponentProp = (component, key) =>
      this.sourceObject?.el?.getAttribute(component)?.[key];

    DIRECTIONAL_LIGHT_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(DIRECTIONAL_LIGHT_COMPONENT, prop),
        set: v => setComponentProp(DIRECTIONAL_LIGHT_COMPONENT, prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of DIRECTIONAL_LIGHT_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new DirectionalLightAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new DirectionalLightEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class DirectionalLightElement extends ScriptableObject {
  static scriptableType = "directional-light";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      
      el.setAttribute("directional-light", {
        color: initial.color || "#ffffff",
        intensity: initial.intensity !== undefined ? initial.intensity : 1,
        castShadow: initial.castShadow !== undefined ? initial.castShadow : true,
        shadowMapResolution: initial.shadowMapResolution || [1024, 1024],
        shadowBias: initial.shadowBias !== undefined ? initial.shadowBias : 0,
        shadowRadius: initial.shadowRadius !== undefined ? initial.shadowRadius : 1
      });

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real DirectionalLightNode and upgrade to scriptable
    const node = createDirectionalLightElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public directional light props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if (prop === "color" && val?.isColor) {
        this[prop] = val.clone();
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of DIRECTIONAL_LIGHT_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of DIRECTIONAL_LIGHT_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if (p === "color" && v?.isColor) {
        init[p] = v.clone();
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    const dest = new DirectionalLightElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor DirectionalLightNode instance to Scriptable, attach adapter and proxies
function makeEditorDirectionalLightNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || DirectionalLightCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: DirectionalLightElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = DirectionalLightElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new DirectionalLightEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new DirectionalLightEditorAdapter(node, descriptors);
  }

  for (const prop of DIRECTIONAL_LIGHT_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  // Ensure DirectionalLightElement's enhanced `clone` logic is used for
  // editor-created directional light nodes. Without this, the instance would fall back to
  // DirectionalLightNode (or Object3D) defaults which do not propagate the
  // scriptable properties managed by the adapter.
  try {
    if (node.clone !== DirectionalLightElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: DirectionalLightElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }
  } catch {
    node.clone = DirectionalLightElement.prototype.clone;
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
DIRECTIONAL_LIGHT_PROPS.forEach(prop => {
  Object.defineProperty(DirectionalLightElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createDirectionalLightElementEditor(initial = {}) {
  const DirectionalLightCtor = (typeof globalThis !== "undefined" && globalThis.DirectionalLightNode) || undefined;
  
  if (typeof DirectionalLightCtor !== "function") {
    throw new Error("DirectionalLightElementEditor: DirectionalLightNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new DirectionalLightCtor(editor);
  } catch (e) {
    throw new Error(`DirectionalLightElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorDirectionalLightNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) {
      // Handle special cases for initial color values
      if (k === "color" && node.color?.isColor) {
        if (typeof initial[k] === "string" || typeof initial[k] === "number") {
          node.color.set(initial[k]);
        } else if (initial[k]?.isColor) {
          node.color.copy(initial[k]);
        }
      } else {
        node[k] = initial[k];
      }
    }
  }
  
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("directional-light", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== DirectionalLightElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return DirectionalLightElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorDirectionalLightNodeScriptable(source);

  return source;
});

/* Named exports expected by external consumers */
export { DirectionalLightAFrameAdapter, DirectionalLightEditorAdapter };

/* Optional: nicer DevTools label */
Object.defineProperty(DirectionalLightElement.prototype, Symbol.toStringTag, { value: "DirectionalLightElement" });